import { useRef, useCallback } from 'react';
import { Method, Header } from '@shopify/network';
import { useNavigationListener } from './navigation-listener.mjs';
import { useLifecycleEventListener } from './lifecycle-event-listener.mjs';

function usePerformanceReport(url, {
  locale = undefined,
  onError = noop
} = {}) {
  const navigations = useRef([]);
  const events = useRef([]);
  const timeout = useRef();
  const sendReport = useCallback(() => {
    if (timeout.current != null) {
      return;
    }

    timeout.current = setTimeout(async () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
        timeout.current = undefined;
      }

      try {
        await fetch(url, {
          method: Method.Post,
          headers: {
            [Header.ContentType]: 'application/json'
          },
          body: JSON.stringify({
            connection: serializableClone(navigator.connection),
            events: events.current,
            navigations: navigations.current.map(navigation => ({
              details: navigation.toJSON({
                removeEventMetadata: false
              }),
              metadata: navigation.metadata
            })),
            pathname: window.location.pathname,
            locale
          })
        });
      } catch (error) {
        if (onError) {
          onError(error);
        }
      } finally {
        events.current = [];
        navigations.current = [];
      }
    }, 1000);
  }, [locale, onError, url]);
  const onNavigation = useCallback(navigation => {
    navigations.current.push(navigation);
    sendReport();
  }, [sendReport]);
  const onLifeCycleEvent = useCallback(event => {
    events.current.push(event);
    sendReport();
  }, [sendReport]);
  useNavigationListener(onNavigation);
  useLifecycleEventListener(onLifeCycleEvent);
}

function serializableClone(object) {
  const output = {}; // We explicitly want to copy the inherited properties
  // eslint-disable-next-line guard-for-in

  for (const key in object) {
    output[key] = object[key];
  }

  return output;
}

function noop() {}

export { usePerformanceReport };
